import { parseAsciiDocHeader, slugify } from "./parseAsciiDocHeader.mjs";

const escapeHtml = (value) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const renderTextStyles = (value) => {
  let html = escapeHtml(value);

  html = html.replace(/\*([^*]+)\*/g, "<strong>$1</strong>");
  html = html.replace(/_([^_]+)_/g, "<em>$1</em>");

  return html;
};

const renderLink = (href, label) => {
  const isInternalTarget = href.startsWith("/") || href.startsWith("#");
  const targetAttributes = isInternalTarget ? "" : ' target="_blank" rel="noreferrer"';

  return `<a href="${escapeHtml(href)}"${targetAttributes}>${renderTextStyles(label)}</a>`;
};

const renderInline = (value) => {
  const tokens = [];
  const stashToken = (html) => {
    const token = `@@BLOGTOKEN${tokens.length}@@`;
    tokens.push({ token, html });
    return token;
  };

  let text = value;

  text = text.replace(/link:([^\s[]+)\[([^\]]+)\]/g, (_, href, label) =>
    stashToken(renderLink(href, label)),
  );

  text = text.replace(/(https?:\/\/[^\s[]+)\[([^\]]+)\]/g, (_, href, label) =>
    stashToken(renderLink(href, label)),
  );

  text = text.replace(/`([^`]+)`/g, (_, code) =>
    stashToken(`<code>${escapeHtml(code)}</code>`),
  );

  text = text.replace(/\+([^+]+)\+/g, (_, code) =>
    stashToken(`<code>${escapeHtml(code)}</code>`),
  );

  let html = renderTextStyles(text);

  for (const token of tokens) {
    html = html.replace(token.token, token.html);
  }

  return html;
};

const stripInlineMarkup = (value) =>
  value
    .replace(/link:[^\s[]+\[([^\]]+)\]/g, "$1")
    .replace(/https?:\/\/[^\s[]+\[([^\]]+)\]/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\+([^+]+)\+/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .trim();

export const parseAsciiDoc = (raw) => {
  const { title, bodyLines } = parseAsciiDocHeader(raw);
  const html = [];
  const headings = [];
  let paragraph = [];
  let listItems = [];
  let listType = "";
  let codeLines = [];
  let inCodeBlock = false;
  let pendingLanguage = "";
  const headingIds = new Map();

  const flushParagraph = () => {
    if (!paragraph.length) {
      return;
    }

    html.push(`<p>${renderInline(paragraph.join(" "))}</p>`);
    paragraph = [];
  };

  const flushList = () => {
    if (!listItems.length) {
      return;
    }

    const tag = listType || "ul";
    html.push(
      `<${tag}>${listItems.map((item) => `<li>${renderInline(item)}</li>`).join("")}</${tag}>`,
    );
    listItems = [];
    listType = "";
  };

  const flushCode = () => {
    if (!inCodeBlock) {
      return;
    }

    const normalizedLanguage = pendingLanguage.trim().toLowerCase();
    const hasVisibleLanguageLabel =
      normalizedLanguage && normalizedLanguage !== "text";
    const languageLabel =
      hasVisibleLanguageLabel
        ? `<div class="blog-code-language">${escapeHtml(pendingLanguage)}</div>`
        : "";
    const toolbarClass = hasVisibleLanguageLabel
      ? "blog-code-toolbar"
      : "blog-code-toolbar blog-code-toolbar-compact";

    html.push(
      `<div class="blog-code-block"><div class="${toolbarClass}">${languageLabel}<button type="button" class="blog-code-copy" aria-label="Copy code block">Copy</button></div><pre><code>${escapeHtml(
        codeLines.join("\n"),
      )}</code></pre></div>`,
    );

    codeLines = [];
    pendingLanguage = "";
    inCodeBlock = false;
  };

  for (const line of bodyLines) {
    const trimmed = line.trim();

    if (trimmed.match(/^\[source,([a-z0-9_+-]+)\]$/i)) {
      flushParagraph();
      flushList();
      pendingLanguage = trimmed.match(/^\[source,([a-z0-9_+-]+)\]$/i)[1];
      continue;
    }

    if (trimmed === "----") {
      if (inCodeBlock) {
        flushCode();
      } else {
        flushParagraph();
        flushList();
        inCodeBlock = true;
        codeLines = [];
      }

      continue;
    }

    if (inCodeBlock) {
      codeLines.push(line);
      continue;
    }

    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }

    const headingMatch = trimmed.match(/^(==+)\s+(.+)$/);

    if (headingMatch) {
      flushParagraph();
      flushList();

      const level = Math.min(headingMatch[1].length, 5);
      const headingLevel = Math.max(2, level);
      const headingText = headingMatch[2].trim();
      const normalizedHeadingText = stripInlineMarkup(headingText);
      const baseId = slugify(normalizedHeadingText) || "section";
      const nextCount = (headingIds.get(baseId) || 0) + 1;
      headingIds.set(baseId, nextCount);
      const headingId = nextCount === 1 ? baseId : `${baseId}-${nextCount}`;

      headings.push({
        id: headingId,
        level: headingLevel,
        text: normalizedHeadingText,
      });

      html.push(
        `<h${headingLevel} id="${headingId}">${renderInline(headingText)}</h${headingLevel}>`,
      );
      continue;
    }

    const imageMatch = trimmed.match(/^image::([^[]+)\[(.*)\]$/);

    if (imageMatch) {
      flushParagraph();
      flushList();

      const [, source, alt] = imageMatch;
      const normalizedSource = source.trim();
      const normalizedAlt = alt.trim();

      html.push(
        `<figure class="blog-image-block"><img src="${escapeHtml(
          normalizedSource,
        )}" alt="${escapeHtml(normalizedAlt)}" loading="lazy" />${
          normalizedAlt ? `<figcaption>${escapeHtml(normalizedAlt)}</figcaption>` : ""
        }</figure>`,
      );
      continue;
    }

    if (trimmed.startsWith("* ") || trimmed.startsWith("- ")) {
      flushParagraph();
      if (listType && listType !== "ul") {
        flushList();
      }
      listType = "ul";
      listItems.push(trimmed.slice(2).trim());
      continue;
    }

    const orderedListMatch = trimmed.match(/^\d+\.\s+(.+)$/);

    if (orderedListMatch) {
      flushParagraph();
      if (listType && listType !== "ol") {
        flushList();
      }
      listType = "ol";
      listItems.push(orderedListMatch[1].trim());
      continue;
    }

    paragraph.push(trimmed);
  }

  flushParagraph();
  flushList();
  flushCode();

  return {
    title,
    headings,
    html: html.join(""),
  };
};
